import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as providerService from "@/shared/api/services/providerService";
import { motion, AnimatePresence } from "framer-motion";
import {
  Server, Search, CheckCircle2, XCircle, AlertTriangle, Clock,
  ArrowDownToLine, ArrowUpFromLine, QrCode, Eye, X, Zap,
} from "lucide-react";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

.mp-root {
  min-height: 100vh;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: #e2f4ff;
  padding: 0 0 48px;
}
.mp-root * { box-sizing: border-box; }

/* ── Header ── */
.mp-h1 { color: #e2f4ff; font-size: 1.6rem; font-weight: 800; letter-spacing: -0.03em; }
.mp-badge-vo {
  background: rgba(34,211,238,0.08); border: 1px solid rgba(34,211,238,0.22);
  color: #22d3ee; border-radius: 999px; padding: 4px 14px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase;
}

/* ── Search ── */
.mp-search-wrap {
  position: relative;
  display: flex; align-items: center;
}
.mp-search-wrap input {
  width: 100%; min-width: 240px;
  background: rgba(34,211,238,0.04);
  border: 1px solid rgba(34,211,238,0.1);
  border-radius: 14px;
  padding: 11px 16px 11px 42px;
  color: #e2f4ff; font-size: 13.5px; font-weight: 500;
  outline: none; font-family: 'Plus Jakarta Sans', sans-serif;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
}
.mp-search-wrap input::placeholder { color: #1e3a55; }
.mp-search-wrap input:focus {
  border-color: rgba(34,211,238,0.4);
  background: rgba(34,211,238,0.06);
  box-shadow: 0 0 0 4px rgba(34,211,238,0.07);
}
.mp-search-icon {
  position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
  color: #2a4a65; pointer-events: none; transition: color 0.2s;
}
.mp-search-wrap input:focus ~ .mp-search-icon,
.mp-search-wrap:focus-within .mp-search-icon { color: #22d3ee; }

/* ── Provider Card ── */
.mp-card {
  background: linear-gradient(145deg, rgba(10,22,46,0.95), rgba(8,18,40,0.98));
  border: 1px solid rgba(34,211,238,0.08);
  border-radius: 24px;
  padding: 0;
  display: flex; flex-direction: column;
  position: relative; overflow: hidden;
  cursor: default; will-change: transform;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
}
.mp-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.5), transparent);
  opacity: 0; transition: opacity 0.3s ease;
}
.mp-card::after {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 80% 60% at 30% 0%, rgba(34,211,238,0.06), transparent 65%);
  opacity: 0; transition: opacity 0.3s ease; pointer-events: none; border-radius: 24px;
}
.mp-card:hover {
  border-color: rgba(34,211,238,0.25);
  box-shadow: 0 0 50px rgba(34,211,238,0.1), 0 20px 60px rgba(0,0,0,0.4);
  transform: translateY(-5px) scale(1.01);
}
.mp-card:hover::before, .mp-card:hover::after { opacity: 1; }

/* Card inner */
.mp-card-inner { padding: 22px; flex: 1; display: flex; flex-direction: column; gap: 0; position: relative; z-index: 1; }

/* Type icon */
.mp-type-icon {
  width: 48px; height: 48px; border-radius: 15px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  position: relative; overflow: hidden;
  transition: box-shadow 0.28s ease, transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
}
.mp-card:hover .mp-type-icon {
  transform: scale(1.1) rotate(-8deg);
  box-shadow: 0 8px 28px rgba(34,211,238,0.3);
}
.mp-type-icon::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(145deg, rgba(255,255,255,0.12), transparent);
  border-radius: 15px;
}

/* Provider name */
.mp-pname { color: #e2f4ff; font-size: 15px; font-weight: 800; letter-spacing: -0.01em; }
.mp-pcode { color: #2a4a60; font-size: 11px; font-family: 'JetBrains Mono', monospace; font-weight: 600; margin-top: 3px; }

/* Status badge */
.mp-status {
  display: inline-flex; align-items: center; gap: 6px;
  border-radius: 999px; padding: 5px 12px;
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.05em; white-space: nowrap;
}
.mp-status-active   { background: rgba(16,185,129,0.1);  color: #34d399; border: 1px solid rgba(16,185,129,0.25); }
.mp-status-inactive { background: rgba(100,116,139,0.1); color: #94a3b8; border: 1px solid rgba(100,116,139,0.2); }
.mp-status-maintenance { background: rgba(245,158,11,0.1); color: #fbbf24; border: 1px solid rgba(245,158,11,0.22); }
.mp-status-degraded { background: rgba(251,146,60,0.1);  color: #fb923c; border: 1px solid rgba(251,146,60,0.22); }

/* Divider */
.mp-div { height: 1px; background: linear-gradient(90deg, transparent, rgba(34,211,238,0.1), transparent); margin: 16px 0; }

/* Stats row */
.mp-stat-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.mp-stat-row:last-child { margin-bottom: 0; }
.mp-stat-lbl { color: #2a4a65; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; }
.mp-stat-val { color: #e2f4ff; font-size: 12.5px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.mp-stat-val.green { color: #34d399; }

/* Success rate bar */
.mp-bar-bg { width: 80px; height: 5px; background: rgba(255,255,255,0.06); border-radius: 99px; overflow: hidden; }
.mp-bar-fill { height: 100%; border-radius: 99px; transition: width 0.8s cubic-bezier(0.34,1.56,0.64,1); }

/* Capability badges */
.mp-cap {
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid rgba(34,211,238,0.14); border-radius: 9px; padding: 5px 12px;
  font-size: 11px; font-weight: 700; color: #3a5a78; cursor: default;
  transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
  position: relative; overflow: hidden;
}
.mp-cap::before {
  content: ''; position: absolute; inset: 0; opacity: 0;
  background: rgba(34,211,238,0.08); transition: opacity 0.2s;
}
.mp-cap:hover { border-color: rgba(34,211,238,0.4); color: #22d3ee; transform: translateY(-2px); box-shadow: 0 4px 14px rgba(34,211,238,0.12); }
.mp-cap:hover::before { opacity: 1; }

/* Empty state */
.mp-empty {
  text-align: center; padding: 80px 24px;
  background: linear-gradient(145deg, rgba(10,22,46,0.5), rgba(8,18,40,0.5));
  border: 1px dashed rgba(34,211,238,0.1); border-radius: 24px;
}
.mp-empty-icon {
  width: 72px; height: 72px; border-radius: 22px; margin: 0 auto 20px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.1);
}

/* Count badge */
.mp-count-badge {
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(34,211,238,0.1); border: 1px solid rgba(34,211,238,0.22);
  border-radius: 999px; padding: 3px 12px; min-width: 32px;
  font-size: 12px; font-weight: 800; color: #22d3ee; font-family: 'JetBrains Mono', monospace;
}

/* Responsive */
@media (max-width: 900px) { .mp-grid { grid-template-columns: repeat(2,1fr) !important; } }
@media (max-width: 580px) { .mp-grid { grid-template-columns: 1fr !important; } .mp-header { flex-direction: column !important; align-items: flex-start !important; } }
@media (max-width: 400px) { .mp-search-wrap input { min-width: 0; } }
`;

const TYPE_GRADIENT = {
  bank:    "linear-gradient(135deg, #0ea5e9, #0284c7)",
  ewallet: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
  qr:      "linear-gradient(135deg, #06b6d4, #0891b2)",
  card:    "linear-gradient(135deg, #f59e0b, #d97706)",
  default: "linear-gradient(135deg, #334155, #1e293b)",
};

const TYPE_BAR_COLOR = {
  bank:    "linear-gradient(90deg, #0ea5e9, #22d3ee)",
  ewallet: "linear-gradient(90deg, #8b5cf6, #a78bfa)",
  qr:      "linear-gradient(90deg, #06b6d4, #22d3ee)",
  card:    "linear-gradient(90deg, #f59e0b, #fbbf24)",
  default: "linear-gradient(90deg, #06b6d4, #22d3ee)",
};

const STATUS_MAP = {
  active:      { cls: "mp-status-active",      Icon: CheckCircle2 },
  inactive:    { cls: "mp-status-inactive",     Icon: XCircle },
  maintenance: { cls: "mp-status-maintenance",  Icon: Clock },
  degraded:    { cls: "mp-status-degraded",     Icon: AlertTriangle },
};

export default function MerchantProviders() {
  const [search, setSearch] = useState("");
  const { language, t } = useLanguage();

  const { data: providers = [], isLoading } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const filtered = providers.filter((p) => {
    const name = getProviderName(p, language) || "";
    return name.toLowerCase().includes(search.toLowerCase()) || p.code?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <MerchantLayout currentPage="MerchantProviders" merchantName="Royal Gaming" merchantCode="MRC001">
      <style>{STYLE}</style>
      <div className="mp-root">
        <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>

          {/* Header */}
          <motion.div
            className="mp-header"
            initial={{ opacity: 0, y: -16, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 15, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                boxShadow: "0 8px 28px rgba(6,182,212,0.4)",
              }}>
                <Server size={22} style={{ color: "#fff" }} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <h1 className="mp-h1">{t("providers")}</h1>
                  <span className="mp-badge-vo">{t("viewOnly")}</span>
                </div>
                <p style={{ color: "#2a4a65", fontSize: 13, fontWeight: 500, marginTop: 2 }}>
                  {filtered.length > 0 ? (
                    <><span className="mp-count-badge" style={{ display: "inline-flex", marginRight: 6 }}>{filtered.length}</span>available providers</>
                  ) : "Payment providers & gateways"}
                </p>
              </div>
            </div>

            {/* Search */}
            <motion.div className="mp-search-wrap" style={{ width: 280 }}
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.38 }}
            >
              <Search size={15} className="mp-search-icon" />
              <input
                placeholder={t("searchProviders") || "Search providers…"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <AnimatePresence>
                {search && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }}
                    onClick={() => setSearch("")}
                    style={{
                      position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                      width: 22, height: 22, borderRadius: 7, border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", color: "#3a5a78",
                    }}
                  >
                    <X size={11} />
                  </motion.button>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>

          {/* Skeleton */}
          {isLoading && (
            <div className="mp-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}
                  style={{ height: 240, borderRadius: 24, background: "linear-gradient(145deg, rgba(10,22,46,0.8), rgba(8,18,40,0.8))", border: "1px solid rgba(34,211,238,0.06)" }}>
                  <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[60, 40, 80, 60].map((w, j) => (
                      <div key={j} style={{ height: j === 0 ? 48 : 12, width: `${w}%`, borderRadius: j === 0 ? 15 : 8, background: "rgba(34,211,238,0.06)", animation: `pulse ${1.4 + j * 0.1}s ease-in-out infinite` }} />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Grid */}
          {!isLoading && (
            <div className="mp-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
              <AnimatePresence>
                {filtered.map((provider, i) => {
                  const sk = (provider.status || "").toLowerCase();
                  const { cls, Icon: StatusIcon } = STATUS_MAP[sk] || STATUS_MAP.active;
                  const rate = provider.success_rate ?? 100;
                  const typeGrad = TYPE_GRADIENT[provider.type] || TYPE_GRADIENT.default;
                  const barGrad = TYPE_BAR_COLOR[provider.type] || TYPE_BAR_COLOR.default;

                  return (
                    <motion.div
                      key={provider.id}
                      className="mp-card"
                      initial={{ opacity: 0, y: 24, scale: 0.96, filter: "blur(6px)" }}
                      animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, scale: 0.94 }}
                      transition={{ delay: i * 0.05, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      whileHover={{ y: -5 }}
                    >
                      {/* Top shimmer */}
                      <motion.div style={{
                        position: "absolute", top: 0, left: 0, right: 0, height: 1, zIndex: 2,
                        background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.6), transparent)",
                      }} />

                      <div className="mp-card-inner">
                        {/* Header row */}
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div className="mp-type-icon" style={{ background: typeGrad }}>
                              <Server size={21} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
                            </div>
                            <div>
                              <div className="mp-pname">{getProviderName(provider, language)}</div>
                              <div className="mp-pcode">{provider.code}</div>
                            </div>
                          </div>
                          <div className={`mp-status ${cls}`}>
                            <StatusIcon size={11} />
                            {t(sk) || sk}
                          </div>
                        </div>

                        <div className="mp-div" />

                        {/* Stats */}
                        <div>
                          <div className="mp-stat-row">
                            <span className="mp-stat-lbl">Type</span>
                            <span className="mp-stat-val" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", textTransform: "capitalize", fontWeight: 700, fontSize: 12 }}>
                              {provider.type || "—"}
                            </span>
                          </div>
                          <div className="mp-stat-row" style={{ alignItems: "center" }}>
                            <span className="mp-stat-lbl">Success Rate</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span className="mp-stat-val green">{rate}%</span>
                              <div className="mp-bar-bg">
                                <motion.div
                                  className="mp-bar-fill"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${rate}%` }}
                                  transition={{ delay: i * 0.05 + 0.3, duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
                                  style={{ background: barGrad, boxShadow: `0 0 6px rgba(34,211,238,0.4)` }}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="mp-stat-row">
                            <span className="mp-stat-lbl">Avg Response</span>
                            <span className="mp-stat-val">{provider.avg_response_time || 0}ms</span>
                          </div>
                        </div>

                        <div className="mp-div" />

                        {/* Capabilities */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {provider.supports_deposit && (
                            <span className="mp-cap">
                              <ArrowDownToLine size={11} />
                              {t("deposits")}
                            </span>
                          )}
                          {provider.supports_withdrawal && (
                            <span className="mp-cap">
                              <ArrowUpFromLine size={11} />
                              {t("withdrawals")}
                            </span>
                          )}
                          {provider.supports_qr && (
                            <span className="mp-cap">
                              <QrCode size={11} />
                              QR
                            </span>
                          )}
                          {!provider.supports_deposit && !provider.supports_withdrawal && !provider.supports_qr && (
                            <span style={{ color: "#2a4a60", fontSize: 12, fontWeight: 500 }}>No capabilities listed</span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          {/* Empty */}
          {!isLoading && filtered.length === 0 && (
            <motion.div className="mp-empty" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <div className="mp-empty-icon">
                <Server size={30} style={{ color: "#1e3a52" }} />
              </div>
              <p style={{ color: "#2a4a65", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                {search ? "No providers match your search" : t("noProvidersFound")}
              </p>
              {search && (
                <button onClick={() => setSearch("")}
                  style={{ marginTop: 12, background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.2)", borderRadius: 10, padding: "8px 20px", color: "#22d3ee", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Clear search
                </button>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </MerchantLayout>
  );
}